// types
import type { FormValues } from '../../../../Typescript/forms';
import {
  Language,
  ILanguageAwareString,
  LanguageAwareString,
} from '../../../../Typescript/classes';

// types and classes
import type { $TSFixMe, GenericObject } from '@calefy-inc/utilityTypes';

/**
 * Takes an object and converts the props_blob properties, leaving the rest untouched. So, {a: 1, props_blob_b: 2} => {a: 1, propsBlob: {b: 2}}
 */
function processPropsBlobProperties(obj: GenericObject) {
  const propsBlobRegex = /^props_blob_(.+)/;
  const result = Object.entries(obj).reduce(
    (acc: $TSFixMe, [k, v]) => {
      if (!v) {
        return acc;
      }
      const propsBlobMatch = propsBlobRegex.exec(k);
      if (propsBlobMatch) {
        acc.propsBlob[propsBlobMatch[1]] = v;
      } else {
        acc[k] = v;
      }
      return acc;
    },
    { propsBlob: {} },
  );
  return result;
}

export function processLanguageAwareProperties(
  obj: GenericObject,
  languages: Array<Language>,
) {
  //console.log('processLanguageAwareProperties: processing', obj);
  const languageMatchRegex = new RegExp(
    `(${languages.map((language) => language.shortName).join('|')})_(.+)`,
  );
  const result = Object.entries(obj).reduce((acc: $TSFixMe, [k, v]) => {
    if (!v) {
      return acc;
    }
    const languageMatch = languageMatchRegex.exec(k);
    if (languageMatch) {
      const language = languages.find(
        (language) => language.shortName === languageMatch[1],
      );
      if (!language) {
        throw new Error(
          `Unable to find matching language with short name ${
            languageMatch[1]
          }: ${JSON.stringify(languages, null, 4)}`,
        );
      }
      const newKey = languageMatch[2];
      if (!acc[newKey]) {
        acc[newKey] = [];
      }
      // @ts-expect-error
      acc[newKey].push(new LanguageAwareString(v, language));
    } else {
      acc[k] = v;
    }
    return acc;
  }, {});

  return result;
}

interface IApportionFormValuesReturn {
  propsBlob: GenericObject;
  [k: string]: $TSFixMe;
}
/**
 * Takes the values from an informed form and processes them into the language-aware fields, propsBlob fields, and the ones that go directly onto the questionInstance
 */
export function apportionFormValues(
  formValues: FormValues,
  languages: Array<Language>,
): IApportionFormValuesReturn {
  //console.log('In apportionFormValues with', formValues, languages);
  // first the language aware properties
  let transformed = processLanguageAwareProperties(formValues, languages);
  transformed = processPropsBlobProperties(transformed);
  // this is necessary for the case where in the propsBlob, there are language-aware properties
  transformed.propsBlob = processLanguageAwareProperties(
    transformed.propsBlob,
    languages,
  );

  // basically checking if this is an input toggle
  // NB This is gross - probably this logic needs to be moved into a subclass of ProgramBuilderQuestionInstance specific to the InputToggle so that we can intelligently enforce the shape of the propsBlob
  if (transformed.propsBlob.options) {
    transformed.propsBlob.options = transformed.propsBlob.options.map(
      (option: $TSFixMe) => {
        const result = processLanguageAwareProperties(option, languages);
        return result;
      },
    );
  }

  return transformed;
}

export function filterPropProperties(formState: $TSFixMe) {
  return Object.entries(formState).reduce((acc, [key, value]) => {
    const propsBlobRegex = /^props_blob_(.+)/;

    if (key.match(propsBlobRegex)) {
      return acc;
    }

    return {
      ...acc,
      [key]: value,
    };
  }, {});
}

export function groupPropProperties(formValues: FormValues) {
  const propsBlob = Object.entries(formValues).reduce((acc, [key, value]) => {
    const propsBlobRegex = /^props_blob_(.+)/;

    const regexMatch = propsBlobRegex.exec(key);

    if (!regexMatch) {
      return acc;
    }

    const capturedPropertyName = regexMatch[1];

    // This will strip the props_blob prefix from the key, and store it in this new obj
    return {
      ...acc,
      [capturedPropertyName]: value,
    };
  }, {});
  return propsBlob;
}

/**
 * Take an object of language aware fields, and transform all fields of the form <languageString>_<desiredName> -> <desiredName>: [{value: form value, language: Language matching the code}]
 */
export function transformLanguageAwareProperties(
  values: {
    [k: string]: $TSFixMe;
  },
  languages: Language[],
): { [k: string]: ILanguageAwareString[] } {
  const languageMatchRegex = new RegExp(
    `(${languages.map((language) => language.shortName).join('|')})_(.+)`,
  );
  // @ts-expect-error
  return Object.entries(values).reduce(
    // @ts-expect-error
    (transformedProperties: { [k: string]: ILanguageAwareString }, entry) => {
      const [key, value] = entry;
      if (typeof value !== 'string') {
        return transformedProperties;
      }
      const match = languageMatchRegex.exec(key);
      if (match) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [_, languageCode, desiredName] = match;
        const matchingLanguage = languages.find(
          (language) => language.shortName === languageCode,
        );
        const transformedValue = {
          value,
          language: matchingLanguage,
        };
        return {
          ...transformedProperties,
          [desiredName]: transformedProperties[desiredName]
            ? // @ts-expect-error
              [...transformedProperties[desiredName], transformedValue]
            : [transformedValue],
        };
      } else {
        return transformedProperties;
      }
    },
    {},
  );
}
